import React, { useState } from 'react';
import { FiMail, FiGithub, FiLinkedin, FiSend, FiCheckCircle, FiAlertCircle } from 'react-icons/fi';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const API_URL = 'https://api.rontaro.com';

const Contact = () => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({ name: '', email: '', message: '' });
    const [status, setStatus] = useState(null); // null | 'loading' | 'success' | 'error'

    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatus('loading');
        try {
            const res = await fetch(`${API_URL}/api/contact`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData),
            });
            if (!res.ok) throw new Error();
            setStatus('success');
            setFormData({ name: '', email: '', message: '' });
        } catch {
            setStatus('error');
        }
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    return (
        <section id="contact" className="section relative">
            {/* Background Orbs */}
            <div className="absolute inset-0 z-0 pointer-events-none">
                <motion.div 
                    animate={{ x: [0, 40, 0], y: [0, 50, 0] }} 
                    transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut' }}
                    className="absolute top-1/4 -right-20 w-96 h-96 bg-amber-200/20 dark:bg-amber-900/10 rounded-full blur-[120px]"
                />
                <motion.div 
                    animate={{ x: [0, -50, 0], y: [0, -40, 0] }} 
                    transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }}
                    className="absolute bottom-0 -left-20 w-80 h-80 bg-blue-200/10 dark:bg-blue-900/5 rounded-full blur-[100px]"
                />
            </div>

            <div className="container-custom relative z-10">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                >
                    {/* Section Header */}
                    <div className="mb-12">
                        <p className="section-number">07 — Contatti</p>
                        <h2 className="section-title">{t('contact.sectionTitle')}</h2>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
                        {/* Contact Info Card */}
                        <motion.div 
                            className="card p-8 lg:p-10 flex flex-col justify-between"
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: 0.2 }}
                        >
                            <div>
                                <h3 className="text-2xl font-semibold mb-8 text-zinc-900 dark:text-zinc-100">
                                    {t('contact.contactInfo')}
                                </h3>

                                <div className="space-y-6 mb-12">
                                    <a
                                        href="mailto:contact@rontaro.com"
                                        className="flex items-center space-x-4 p-4 rounded-2xl bg-white/50 dark:bg-zinc-800/30 border border-white/40 dark:border-zinc-700/30 hover:border-amber-500/50 transition-all group"
                                    >
                                        <div className="p-3 rounded-xl bg-amber-500/10 text-amber-600 dark:text-amber-400 group-hover:scale-110 transition-transform">
                                            <FiMail size={24} />
                                        </div>
                                        <span className="text-lg text-zinc-600 dark:text-zinc-400 font-medium">contact@rontaro.com</span>
                                    </a>
                                </div>
                            </div>

                            <div>
                                <h4 className="text-sm font-mono uppercase tracking-widest text-zinc-400 dark:text-zinc-500 mb-6">
                                    {t('contact.followMe')}
                                </h4>
                                <div className="flex space-x-4">
                                    <a
                                        href="https://github.com/Rontaro"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="p-4 card hover:border-amber-400 hover:text-amber-600 dark:hover:text-amber-400 transition-all"
                                        aria-label="GitHub"
                                    >
                                        <FiGithub size={24} />
                                    </a>
                                    <a
                                        href="https://linkedin.com/in/antoromano"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="p-4 card hover:border-amber-400 hover:text-amber-600 dark:hover:text-amber-400 transition-all"
                                        aria-label="LinkedIn"
                                    >
                                        <FiLinkedin size={24} />
                                    </a>
                                </div>
                            </div>
                        </motion.div>

                        {/* Contact Form Card */}
                        <motion.div 
                            className="card p-8 lg:p-10"
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: 0.3 }}
                        >
                            <form onSubmit={handleSubmit} className="space-y-6">
                                <div>
                                    <label htmlFor="name" className="block text-sm font-medium mb-2 ml-1 text-zinc-500">
                                        {t('contact.form.name')}
                                    </label>
                                    <input
                                        type="text"
                                        id="name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        required
                                        className="glass-input"
                                        placeholder={t('contact.form.namePlaceholder')}
                                    />
                                </div>

                                <div>
                                    <label htmlFor="email" className="block text-sm font-medium mb-2 ml-1 text-zinc-500">
                                        {t('contact.form.email')}
                                    </label>
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        required
                                        className="glass-input"
                                        placeholder={t('contact.form.emailPlaceholder')}
                                    />
                                </div>

                                <div>
                                    <label htmlFor="message" className="block text-sm font-medium mb-2 ml-1 text-zinc-500">
                                        {t('contact.form.message')}
                                    </label>
                                    <textarea
                                        id="message"
                                        name="message"
                                        value={formData.message}
                                        onChange={handleChange}
                                        required
                                        rows="4"
                                        className="glass-input resize-none"
                                        placeholder={t('contact.form.messagePlaceholder')}
                                    />
                                </div>

                                {status === 'success' && (
                                    <motion.div 
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className="flex items-center gap-2 p-4 rounded-xl bg-green-500/10 text-green-600 dark:text-green-400 text-sm font-medium border border-green-500/20"
                                    >
                                        <FiCheckCircle size={18} />
                                        <span>{t('contact.form.successMessage')}</span>
                                    </motion.div>
                                )}
                                {status === 'error' && (
                                    <motion.div 
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className="flex items-center gap-2 p-4 rounded-xl bg-red-500/10 text-red-600 dark:text-red-400 text-sm font-medium border border-red-500/20"
                                    >
                                        <FiAlertCircle size={18} />
                                        <span>{t('contact.form.errorMessage')}</span>
                                    </motion.div>
                                )}
                                <button
                                    type="submit"
                                    disabled={status === 'loading' || status === 'success'}
                                    className="w-full btn-primary flex items-center justify-center space-x-2 disabled:opacity-60 disabled:cursor-not-allowed group py-4"
                                >
                                    <span className="text-base font-bold">{status === 'loading' ? t('contact.form.sending') : t('contact.form.sendMessage')}</span>
                                    <FiSend size={20} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                                </button>
                            </form>
                        </motion.div>
                    </div>
                </motion.div>
            </div>
        </section>

    );
};

export default Contact;
